import { Key, memo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { TmdxSelect } from '@ds';
import { AdminFormRow } from '@ui';
import { ContactFormType } from '@/types';
import { renderSelectItem } from '@/utils/helpers';
import { ClearSelectButton } from '../ClearSelectButton';

type PrimaryContactFormValues = ContactFormType & { primaryContactId: string | null };

type ContactSelectorProps = {
  selectItems: { value: string; name: string }[];
  isDisabled?: boolean;
  isRequired?: boolean;
  isClearableForm?: boolean;
  onContactChange: (id: Key) => void;
  clearPrimaryContact: () => void;
};

const handlePrimaryContactSelect = (
  selectedValue: string,
  onChange: (value: string | null) => void,
  onContactChange: (id: Key) => void,
) => {
  onChange(selectedValue);
  onContactChange(selectedValue);
};

const handlePrimaryContactClear = (onChange: (value: string | null) => void, clearPrimaryContact: () => void) => {
  onChange(null);
  clearPrimaryContact();
};

export const ContactSelector = memo(
  ({
    selectItems,
    isDisabled,
    isRequired,
    isClearableForm,
    onContactChange,
    clearPrimaryContact,
  }: ContactSelectorProps) => {
    const { control } = useFormContext<PrimaryContactFormValues>();

    return (
      <AdminFormRow title="Select contact" isRequired={isRequired} inputWrapperClass="max-w-190">
        <Controller
          name="primaryContactId"
          control={control}
          render={({ field: { ref, value, onChange, ...field } }) => {
            const selectedKey = value ?? '';
            const selectedKeys = selectedKey ? [selectedKey] : [];
            const hasValidSelection = !!selectedKey && selectItems.some((item) => item.value === selectedKey);

            return (
              <TmdxSelect
                placeholder="Choose contact"
                aria-label="Primary Contact"
                selectedKeys={selectedKeys}
                disallowEmptySelection={false}
                isDisabled={isDisabled}
                items={selectItems}
                endContent={
                  isClearableForm && hasValidSelection && !isDisabled ? (
                    <ClearSelectButton
                      disabled={isDisabled}
                      onClick={() => handlePrimaryContactClear(onChange, clearPrimaryContact)}
                    />
                  ) : undefined
                }
                onChange={(e) => handlePrimaryContactSelect(e.target.value, onChange, onContactChange)}
                {...field}
              >
                {renderSelectItem}
              </TmdxSelect>
            );
          }}
        />
      </AdminFormRow>
    );
  },
);
